import type { ProductContextId } from '@vs-shop/schemas/id/ProductsContextId';
import type {
  CheckboxFilterStateMap,
  CheckboxFilterValue,
} from '@vs-shop/schemas/product/filter/state/checkbox';
import { Context, Effect, HashMap, Layer, Option, Stream, SubscriptionRef } from 'effect';

import { ContextTag } from '../context';

export type ContextualCheckboxFilterStateMap = HashMap.HashMap<
  ProductContextId,
  CheckboxFilterStateMap
>;

// ===== EXPORTED UTILITIES =====
// Standalone stream utilities for atom usage

export const emptyFilterMap = HashMap.empty<string, CheckboxFilterValue>();

export const createCheckboxFiltersStream = (
  ref: SubscriptionRef.SubscriptionRef<ContextualCheckboxFilterStateMap>,
  context: ProductContextId
) => {
  return SubscriptionRef.changes(ref).pipe(
    Stream.changes,
    Stream.map((contextMap) => HashMap.get(contextMap, context)),
    Stream.map(Option.getOrElse(() => emptyFilterMap)),
    Stream.changes
  );
};

export const createCheckboxFilterByCodeStream = (
  ref: SubscriptionRef.SubscriptionRef<ContextualCheckboxFilterStateMap>,
  context: ProductContextId,
  attributeCode: string
) =>
  SubscriptionRef.changes(ref).pipe(
    Stream.map((contextMap) => HashMap.get(contextMap, context)),
    Stream.map(Option.flatMap((filters) => HashMap.get(filters, attributeCode)))
  );

export const createCheckboxIsSelectedStream = (
  ref: SubscriptionRef.SubscriptionRef<ContextualCheckboxFilterStateMap>,
  context: ProductContextId,
  attributeCode: string,
  value: CheckboxFilterValue
) =>
  createCheckboxFilterByCodeStream(ref, context, attributeCode).pipe(
    Stream.map(
      Option.match({
        onNone: () => false,
        onSome: (selectedValue) => selectedValue === value,
      })
    )
  );

export const createCheckboxHasActiveFiltersStream = (
  ref: SubscriptionRef.SubscriptionRef<ContextualCheckboxFilterStateMap>,
  context: ProductContextId
) =>
  createCheckboxFiltersStream(ref, context).pipe(
    Stream.map((filters) => HashMap.size(filters) > 0)
  );

export class CheckboxFilterService extends Context.Service<CheckboxFilterService>()(
  ContextTag.CheckboxFilterService,
  {
    make: Effect.gen(function* () {
      const emptyContextMap = HashMap.empty<ProductContextId, CheckboxFilterStateMap>();

      const filtersRef =
        yield* SubscriptionRef.make<ContextualCheckboxFilterStateMap>(emptyContextMap);

      const getFilters = (context: ProductContextId) =>
        Effect.gen(function* () {
          const contextMap = yield* SubscriptionRef.get(filtersRef);
          return HashMap.get(contextMap, context).pipe(Option.getOrElse(() => emptyFilterMap));
        });

      const getFilterByCode = (context: ProductContextId, attributeCode: string) =>
        Effect.gen(function* () {
          const contextFilters = yield* getFilters(context);
          return HashMap.get(contextFilters, attributeCode);
        });

      const setFilter = (
        context: ProductContextId,
        attributeCode: string,
        value: CheckboxFilterValue
      ) =>
        SubscriptionRef.update(filtersRef, (contextMap) => {
          const currentContextFilters = HashMap.get(contextMap, context).pipe(
            Option.getOrElse(() => emptyFilterMap)
          );
          const updatedContextFilters = HashMap.set(currentContextFilters, attributeCode, value);
          return HashMap.set(contextMap, context, updatedContextFilters);
        });

      const removeFilter = (context: ProductContextId, attributeCode: string) =>
        SubscriptionRef.update(filtersRef, (contextMap) => {
          const currentContextFilters = HashMap.get(contextMap, context).pipe(
            Option.getOrElse(() => emptyFilterMap)
          );
          const updatedContextFilters = HashMap.remove(currentContextFilters, attributeCode);

          if (HashMap.isEmpty(updatedContextFilters)) {
            return HashMap.remove(contextMap, context);
          }

          return HashMap.set(contextMap, context, updatedContextFilters);
        });

      const clearFilters = (context: ProductContextId) =>
        SubscriptionRef.update(filtersRef, HashMap.remove(context));

      const setAllFilters = (context: ProductContextId, filters: CheckboxFilterStateMap) =>
        SubscriptionRef.update(filtersRef, HashMap.set(context, filters));

      // ===== STREAMING METHODS =====
      // Backward compatible wrappers using exported utilities

      const getFiltersStream = (context: ProductContextId) =>
        createCheckboxFiltersStream(filtersRef, context);

      const getFilterByCodeStream = (context: ProductContextId, attributeCode: string) =>
        createCheckboxFilterByCodeStream(filtersRef, context, attributeCode);

      const isSelectedStream = (
        context: ProductContextId,
        attributeCode: string,
        value: CheckboxFilterValue
      ) => createCheckboxIsSelectedStream(filtersRef, context, attributeCode, value);

      const hasActiveFiltersStream = (context: ProductContextId) =>
        createCheckboxHasActiveFiltersStream(filtersRef, context);

      return {
        filtersRef,
        // Effect-based methods (existing)
        getFilters,
        getFilterByCode,
        setFilter,
        removeFilter,
        clearFilters,
        setAllFilters,
        // Stream-based methods (new)
        getFiltersStream,
        getFilterByCodeStream,
        isSelectedStream,
        hasActiveFiltersStream,
      };
    }),
  }
) {
  static readonly layer = Layer.effect(this, this.make);
}
